import { IPromise, IQService, IRootScopeService } from "angular";
import { IGoal } from "../models";
import { IUsersIdMap } from "@gtmhub/users/models";
import { GoalService } from "../goal.service";
import { UserService } from "@gtmhub/users/userService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IStateInit } from "@gtmhub/core/routing";
import { IModalScope } from "angular-ui-bootstrap";
import { OkrEvents } from "@gtmhub/okrs";

interface IReviewParamsService {
  id: string;
  action: string;
}

interface IReviewScope extends IModalScope {
  indicators: {
    loading: boolean;
    approving: boolean;
    declining: boolean;
    withdrawing: boolean;
  };
  goal: IGoal;
  usersIdMap: IUsersIdMap;
  mode: string;
  comment: {
    approve: string;
    decline: string;
  };
  approve(): void;
  decline(): void;
  withdraw(): void;
}

export class ReviewCtrl implements IStateInit {
  private goalFields = "name,workflow{status,reviewersNeeded,reviewers,approvedAt}";
  private okrEvents: OkrEvents;

  private strings = {
    loading: "loading",
    approving: "approving",
    declining: "declining",
    withdrawing: "withdrawing",
    goal: "goal",
    usersIdMap: "usersIdMap",
  };

  public static $inject = ["$rootScope", "$scope", "$stateParams", "$q", "GoalService", "UserService", "UIErrorHandlingService"];

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: IReviewScope,
    private $stateParams: IReviewParamsService,
    private $q: IQService,
    private goalService: GoalService,
    private userService: UserService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.okrEvents = new OkrEvents(this.$scope);
  }

  stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {
      loading: true,
      approving: false,
      declining: false,
      withdrawing: false,
    };

    this.$scope.comment = {
      approve: "",
      decline: "",
    };

    this.$scope.mode = this.$stateParams.action;

    this.$scope.approve = this.approve;
    this.$scope.decline = this.decline;
    this.$scope.withdraw = this.withdraw;

    return this.$q
      .all({
        goal: this.goalService.getGoalWithFields(this.$stateParams.id, this.goalFields),
        usersIdMap: this.userService.getUsersIdMap(),
      })
      .then(
        ({ goal, usersIdMap }) => {
          this.$scope.goal = goal;
          this.$scope.usersIdMap = usersIdMap;

          this.$scope.indicators.loading = false;
        },
        (err) => this.handleIndicatorError(err, this.strings.loading)
      );
  };

  private approve = (): void => {
    this.$scope.indicators.approving = true;

    this.goalService.approveGoal(this.$scope.goal.id, this.$scope.comment.approve).then(
      () => {
        this.$scope.indicators.approving = false;

        this.broadcastGoalChanged();

        if (this.$scope.comment.approve) {
          this.broadcastCommentPostedExternally();
        }

        this.$rootScope.$broadcast("approvedGoal", this.$scope.goal);

        this.$scope.$close();
      },
      (err) => this.handleIndicatorError(err, this.strings.approving, true)
    );
  };

  private decline = (): void => {
    this.$scope.indicators.declining = true;

    this.goalService.declineGoal(this.$scope.goal.id, this.$scope.comment.decline).then(
      () => {
        this.$scope.indicators.declining = false;

        this.broadcastGoalChanged();
        this.broadcastCommentPostedExternally();

        this.$scope.$close();
      },
      (err) => this.handleIndicatorError(err, this.strings.declining, true)
    );
  };

  private withdraw = (): void => {
    this.$scope.indicators.withdrawing = true;

    this.goalService.withdrawGoalFromReview(this.$scope.goal.id).then(
      () => {
        this.$scope.indicators.withdrawing = false;

        this.broadcastGoalChanged();

        this.$scope.$close();
      },
      (err) => this.handleIndicatorError(err, this.strings.declining, true)
    );
  };

  private broadcastGoalChanged = (): void => {
    this.okrEvents.broadcastGoalsChanged("goalChanged");
    this.$rootScope.$broadcast("goalsChanged");
  };

  private broadcastCommentPostedExternally = (): void => {
    this.$rootScope.$broadcast("commentPostedExternally", this.$scope.goal.id, "goal");
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };
}
